import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, CheckCircle2, XCircle, Clock3, AlertTriangle, Filter } from 'lucide-react';
import { api } from '../../lib/api.ts';
import { Button } from '../ui/Button.tsx';
import { Badge } from '../ui/Badge.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ActionLog {
  id: string;
  instanceId: string;
  customerId: string;
  actionType: string;
  stepId: string | null;
  stepName: string | null;
  actionInput: Record<string, unknown> | null;
  actionOutput: Record<string, unknown> | null;
  status: 'success' | 'failed' | 'skipped';
  errorMessage: string | null;
  executedAt: string;
}

interface InstanceLogModalProps {
  automationId: string;
  instanceId: string;
  customerId: string;
  status: string;
  startedAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
  onClose: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Parses a Lambda-style error JSON (`{"errorType":"Error","errorMessage":"...","trace":[...]}`)
 * and extracts the human-readable message. Falls back to the raw string for plain errors.
 */
function parseErrorMessage(raw: string): { display: string; isJson: boolean } {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object' && 'errorMessage' in parsed && typeof (parsed as Record<string, unknown>).errorMessage === 'string') {
      return { display: (parsed as Record<string, unknown>).errorMessage as string, isJson: true };
    }
  } catch {
    // Not JSON — show as-is
  }
  return { display: raw, isJson: false };
}

/** Nil UUID used as customerId for bulk (non-fan-out) schedule executions. */
const BULK_CUSTOMER_ID = '00000000-0000-0000-0000-000000000000';

function fmtTime(iso: string) {
  try {
    return new Date(iso).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

function fmtDateTime(iso: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const INSTANCE_STATUS_COLORS: Record<string, string> = {
  completed: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-red-100 text-red-700',
  running: 'bg-purple-100 text-purple-700',
  pending: 'bg-amber-100 text-amber-700',
  paused: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-zinc-100 text-zinc-500',
};

/** Human-readable labels for known action log fields */
const FIELD_LABELS: Record<string, string> = {
  templateId: 'Template',
  recipientAddress: 'To',
  messageLogId: 'Message ID',
  status: 'Status',
  channel: 'Channel',
  segmentId: 'Segment',
  campaignId: 'Campaign ID',
  points: 'Points',
  programSlug: 'Program',
  newBalance: 'New balance',
  key: 'Attribute',
  value: 'Value',
  correlationKey: 'Wait for',
  timeoutSeconds: 'Timeout',
  durationSeconds: 'Duration',
  result: 'Result',
  hasConsent: 'Has consent',
  sfnStatus: 'Execution status',
  executionArn: 'Execution ARN',
  automationDefinitionId: 'Automation',
  condition: 'Condition',
};

const STATUS_EMOJI: Record<string, string> = {
  sent: '✓ Sent',
  queued: '⏳ Queued',
  failed: '✕ Failed',
  success: '✓',
};

function HumanReadableData({ data, label }: { data: Record<string, unknown> | null; label: 'in' | 'out' }) {
  if (!data || Object.keys(data).length === 0) return null;
  // Filter out UUIDs and internal fields for cleaner display
  const entries = Object.entries(data).filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (entries.length === 0) return null;

  return (
    <div className="mt-1">
      {entries.map(([k, v]) => {
        const humanLabel = FIELD_LABELS[k] ?? k.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
        let displayValue: string;
        if (k === 'status' && typeof v === 'string') {
          displayValue = STATUS_EMOJI[v] ?? v;
        } else if (typeof v === 'boolean') {
          displayValue = v ? 'Yes' : 'No';
        } else if (typeof v === 'object') {
          displayValue = JSON.stringify(v);
        } else {
          displayValue = String(v);
        }
        // Truncate long UUIDs for readability
        if (typeof displayValue === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-/.test(displayValue) && displayValue.length > 20) {
          displayValue = displayValue.slice(0, 8) + '…';
        }
        return (
          <div key={k} className="flex items-baseline gap-1.5 text-[10px]">
            <span className={`shrink-0 font-medium ${label === 'in' ? 'text-blue-400' : 'text-emerald-400'}`}>
              {humanLabel}:
            </span>
            <span className="text-zinc-600 break-all">{displayValue}</span>
          </div>
        );
      })}
    </div>
  );
}

const STATUS_BADGE_VARIANT: Record<string, 'success' | 'danger' | 'default'> = {
  success: 'success',
  failed: 'danger',
  skipped: 'default',
};

/** Human-readable names for action types */
const ACTION_LABELS: Record<string, string> = {
  journey_started: 'Journey Started',
  journey_completed: 'Journey Completed',
  journey_failed: 'Journey Failed',
  send_email: 'Send Email',
  send_sms: 'Send SMS',
  send_campaign: 'Send Campaign',
  add_points: 'Add Points',
  remove_points: 'Remove Points',
  issue_coupon: 'Issue Coupon',
  update_attribute: 'Update Attribute',
  check_consent: 'Check Consent',
  condition: 'Condition',
  wait_duration: 'Wait',
  wait_event_start: 'Wait for Event',
  wait_event_resolved: 'Event Received',
};

// ─── Component ────────────────────────────────────────────────────────────────

export function InstanceLogModal({
  automationId,
  instanceId,
  customerId,
  status,
  startedAt,
  completedAt,
  errorMessage,
  onClose,
}: InstanceLogModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const [showOnlyFailed, setShowOnlyFailed] = useState(false);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Focus close button on open
  useEffect(() => { closeRef.current?.focus(); }, []);

  // Reset filter when opening a new instance
  useEffect(() => { setShowOnlyFailed(false); }, [instanceId]);

  const { data: logs, isLoading } = useQuery<ActionLog[]>({
    queryKey: ['instance-logs', automationId, instanceId],
    queryFn: () =>
      api
        .get(`/api/v1/automations/${automationId}/instance-logs`, { params: { instanceId } })
        .then((r) => r.data),
    enabled: !!instanceId,
  });

  const hasFailed = (logs ?? []).some((l) => l.status === 'failed');
  const failedCount = (logs ?? []).filter((l) => l.status === 'failed').length;
  const visibleLogs = showOnlyFailed
    ? (logs ?? []).filter((l) => l.status === 'failed')
    : (logs ?? []);

  return (
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="instance-log-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-xl border border-zinc-200 flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-zinc-100 shrink-0">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 id="instance-log-title" className="text-sm font-semibold text-zinc-900">Instance log</h2>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${INSTANCE_STATUS_COLORS[status] ?? 'bg-zinc-100 text-zinc-500'}`}
              >
                {status}
              </span>
              {(logs ?? []).length > 0 && (
                <span className="text-[10px] text-zinc-400 font-medium">
                  {(logs ?? []).length} step{(logs ?? []).length > 1 ? 's' : ''} recorded
                </span>
              )}
              {hasFailed && (
                <span className="inline-flex items-center gap-1 text-[10px] text-red-500 font-medium">
                  <AlertTriangle size={10} />
                  {failedCount} failed
                </span>
              )}
            </div>
            <p className="text-[11px] text-zinc-400 font-mono mt-0.5 truncate">
              {customerId === BULK_CUSTOMER_ID ? 'customer: — (bulk execution)' : `customer: ${customerId}`}
            </p>
            <div className="flex items-center gap-3 mt-1.5 flex-wrap">
              <span className="text-[10px] text-zinc-400">
                <span className="font-medium text-zinc-500">Start:</span>{' '}
                {fmtDateTime(startedAt)}
              </span>
              {completedAt && (
                <span className="text-[10px] text-zinc-400">
                  <span className="font-medium text-zinc-500">End:</span>{' '}
                  {fmtDateTime(completedAt)}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors ml-3 shrink-0"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Error banner (instance failed) */}
        {status === 'failed' && errorMessage && (() => {
          const err = parseErrorMessage(errorMessage);
          return (
            <div className="mx-5 mt-3 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg flex items-start gap-2 shrink-0">
              <AlertTriangle size={13} className="text-red-400 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-[11px] text-red-600 break-all">{err.display}</p>
                {err.isJson && (
                  <details className="mt-1">
                    <summary className="text-[10px] text-red-400 cursor-pointer select-none">Show raw error</summary>
                    <pre className="text-[10px] font-mono mt-1 text-red-500 whitespace-pre-wrap break-all">{errorMessage}</pre>
                  </details>
                )}
              </div>
            </div>
          );
        })()}

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 py-4">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-14 bg-zinc-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : !logs || logs.length === 0 ? (
            <div className="py-12 text-center">
              <Clock3 size={24} className="text-zinc-300 mx-auto mb-2" />
              <p className="text-sm text-zinc-400">No step logs recorded</p>
              <p className="text-[11px] text-zinc-300 mt-1 max-w-sm mx-auto">
                The execution may not have started yet, or the Step Functions execution
                failed before any step handler ran.
              </p>
            </div>
          ) : (
            <>
              {/* Filter toolbar */}
              {hasFailed && (
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[11px] text-zinc-400">
                    {visibleLogs.length} / {logs.length} step{logs.length > 1 ? 's' : ''}
                  </span>
                  <button
                    onClick={() => setShowOnlyFailed((v) => !v)}
                    className={`flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-lg border font-medium transition-colors ${
                      showOnlyFailed
                        ? 'bg-red-50 border-red-200 text-red-600'
                        : 'border-zinc-200 text-zinc-500 hover:border-zinc-300 hover:text-zinc-700'
                    }`}
                  >
                    <Filter size={10} />
                    {showOnlyFailed ? 'Showing failed only' : 'Show only failed'}
                  </button>
                </div>
              )}

              <ol className="relative border-l border-zinc-100 ml-3 space-y-0">
                {visibleLogs.map((log, idx) => (
                  <li key={log.id} className="pl-6 pb-5 relative">
                    {/* Timeline dot */}
                    <span
                      className={`absolute left-[-7px] top-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full border-2 border-white ${
                        log.status === 'success'
                          ? 'bg-emerald-400'
                          : log.status === 'failed'
                          ? 'bg-red-400'
                          : 'bg-amber-300'
                      }`}
                      aria-hidden="true"
                    >
                      {log.status === 'success' ? (
                        <CheckCircle2 size={8} className="text-white" />
                      ) : log.status === 'failed' ? (
                        <XCircle size={8} className="text-white" />
                      ) : (
                        <Clock3 size={8} className="text-white" />
                      )}
                    </span>

                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-semibold text-zinc-800">
                            {log.stepName || ACTION_LABELS[log.actionType] || log.actionType}
                          </span>
                          {log.stepName && log.actionType !== log.stepName && (
                            <span className="text-[10px] text-zinc-400">{ACTION_LABELS[log.actionType] ?? log.actionType}</span>
                          )}
                          <Badge variant={STATUS_BADGE_VARIANT[log.status] ?? 'default'}>
                            {log.status}
                          </Badge>
                        </div>

                        {/* Input */}
                        <HumanReadableData data={log.actionInput} label="in" />

                        {/* Output */}
                        <HumanReadableData data={log.actionOutput} label="out" />

                        {/* Error */}
                        {log.errorMessage && (() => {
                          const err = parseErrorMessage(log.errorMessage);
                          return (
                            <div className="mt-1.5 flex items-start gap-1.5 px-2 py-1.5 bg-red-50 rounded-md border border-red-100">
                              <XCircle size={10} className="text-red-400 mt-0.5 shrink-0" />
                              <div className="min-w-0 flex-1">
                                <span className="text-[11px] text-red-600 break-all">{err.display}</span>
                                {err.isJson && (
                                  <details className="mt-0.5">
                                    <summary className="text-[10px] text-red-400 cursor-pointer select-none">Show raw error</summary>
                                    <pre className="text-[10px] font-mono mt-0.5 text-red-500 whitespace-pre-wrap break-all">{log.errorMessage}</pre>
                                  </details>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                      <span className="text-[10px] text-zinc-400 whitespace-nowrap font-mono shrink-0">
                        {fmtTime(log.executedAt)}
                      </span>
                    </div>

                    {idx === visibleLogs.length - 1 && (
                      <span className="sr-only">End of log</span>
                    )}
                  </li>
                ))}
              </ol>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-5 py-3.5 border-t border-zinc-100 shrink-0">
          <Button ref={closeRef} size="sm" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
